import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Stack,
    IconButton,
    Button,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    FormControlLabel,
    FormLabel,
    RadioGroup,
    Radio,
    Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDataProvider, Title, Loading, useNotify } from 'react-admin';
import { useQueryClient } from 'react-query';
import { REACT_APP_SUPABASE_ANON_KEY } from '../lib/constants';
import { useQuery } from 'react-query';
import { supabase } from '../lib/supabaseClient';
import PhoneIcon from '@mui/icons-material/Phone';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionIcon from '@mui/icons-material/Description';
import TablePagination from '@mui/material/TablePagination';
import { LeadDrawer } from '../leads/LeadShow';
import { CreateLeadDialog } from '../leads/CreateLeadDialog';
import { LeadsTriage } from '../leads/triage/LeadsTriage';
import { channelLabel, channelMatchesSource } from './funnelSummary';
import type { SourceFilter } from './funnelSummary';
import AddIcon from '@mui/icons-material/Add';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

// Tab indexes, in display order. Triage (T-208) is first and the default
// (T-219); refer to tabs by name so reordering them stays a one-line change.
const TAB = {
    triage: 0,
    leadsList: 1,
    qualifiedFollowUps: 2,
    callFunnel: 3,
    leadFunnel: 4,
    leadQualification: 5,
    campaignPerformance: 6,
    conversionRates: 7,
    dailyMetrics: 8,
} as const;

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`analytics-tabpanel-${index}`}
            aria-labelledby={`analytics-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

// Aircall Button Component
const AircallButton = ({ phoneNumber }: { phoneNumber: string }) => {
    if (!phoneNumber) return null;
    
    const handleAircallClick = () => {
        window.open(`aircall:${phoneNumber}`, '_self');
    };

    return (
        <IconButton
            onClick={handleAircallClick}
            size="small"
            sx={{ 
                ml: 1,
                '& img': {
                    width: 20,
                    height: 20,
                }
            }}
            title={`Call ${phoneNumber}`}
        >
            <img src="/aircall.png" alt="Call" />
        </IconButton>
    );
};

export const AnalyticsDashboard = () => {
    const [tabValue, setTabValue] = useState<number>(TAB.triage);
    const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
    const [pipelineFilter, setPipelineFilter] = useState<{ bucket: string; mode: 'qualified' | 'open'; filterType?: 'pipeline' | 'agent' | 'language' } | null>(null);
    const [breakdownTab, setBreakdownTab] = useState(0);
    const [qualifiedSince, setQualifiedSince] = useState<'month' | '30' | '60' | '90' | 'all'>('all');
    const [typeFilter, setTypeFilter] = useState<'all' | 'at_need' | 'pre_need'>('all');
    const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'won' | 'lost'>('all');
    const [regionFilter, setRegionFilter] = useState<'all' | 'quebec' | 'ontario'>('all');
    const [languageFilter, setLanguageFilter] = useState<'all' | 'en' | 'fr'>('all');
    const [stageFilter, setStageFilter] = useState<'all' | 'new' | 'spoken' | 'qualified' | 'disqualified'>('all');
    const [sourceTypeFilter, setSourceTypeFilter] = useState<'all' | 'call' | 'form'>('all');
    const [closeLostLead, setCloseLostLead] = useState<any>(null);
    const [drawerLeadId, setDrawerLeadId] = useState<string | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [breakdownExpanded, setBreakdownExpanded] = useState(false);
    const [qualifiedPage, setQualifiedPage] = useState(0);
    const [leadsPage, setLeadsPage] = useState(0);
    const rowsPerPage = 50;
    const [closeLostReason, setCloseLostReason] = useState('');
    const [closeLostNotes, setCloseLostNotes] = useState('');
    const now = new Date();
    const [selectedMonth, setSelectedMonth] = useState(() => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const SUPABASE_URL = (process.env.REACT_APP_SUPABASE_PROD_URL || process.env.REACT_APP_SUPABASE_URL || '');
    const queryClient = useQueryClient();
    // `?newLead=1` opens the dialog straight away. The Slack message for a call
    // from a hospital has no qualify buttons — there is no lead to qualify — so
    // it links here instead, and the agent enters the family's number rather
    // than the switchboard's.
    const [createLeadOpen, setCreateLeadOpen] = useState(
        () => new URLSearchParams(window.location.search).get('newLead') === '1',
    );

    const isCurrentMonth = selectedMonth === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const handlePrevMonth = () => {
        const [y, m] = selectedMonth.split('-').map(Number);
        const d = new Date(y, m - 2, 1);
        setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    };
    const handleNextMonth = () => {
        const [y, m] = selectedMonth.split('-').map(Number);
        const d = new Date(y, m, 1);
        setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    };
    const selectedMonthLabel = (() => {
        const [y, m] = selectedMonth.split('-').map(Number);
        return new Date(y, m - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
    })();

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleSourceFilterChange = (filter: SourceFilter) => {
        setSourceFilter(filter);
    };

    // Filter/aggregate data based on source channel
    const filterBySource = (data: any[] | undefined) => {
        if (!data) return data;
        
        // Get the grouping key (e.g., 'week', 'campaign', etc.)
        const groupKey = data.length > 0 ? Object.keys(data[0]).find(k => k !== 'source_channel' && k !== 'id') : null;
        if (!groupKey) return data;
        
        const isSummable = (field: string, value: unknown) =>
            field !== groupKey && field !== 'source_channel' && field !== 'id' && typeof value === 'number';

        // Sum the matching channels into one row per key ('maps' spans both
        // maps_gbp_* channels; 'all' spans every channel).
        const grouped = new Map();
        data.filter(item => channelMatchesSource(item.source_channel, sourceFilter)).forEach(item => {
            const key = item[groupKey];
            if (!grouped.has(key)) {
                grouped.set(key, { ...item, source_channel: sourceFilter });
            } else {
                const existing = grouped.get(key);
                Object.keys(item).forEach(field => {
                    if (isSummable(field, item[field])) {
                        existing[field] = (existing[field] || 0) + item[field];
                    }
                });
            }
        });

        if (sourceFilter === 'all') return Array.from(grouped.values());

        // Keep every key from the full dataset (e.g. all weeks), zero-filling
        // the ones this source has no row for.
        const allKeys = [...new Set(data.map(item => item[groupKey]))];
        return allKeys.map(key => {
            if (grouped.has(key)) return grouped.get(key);
            const zeroRow: any = { [groupKey]: key, source_channel: sourceFilter };
            Object.keys(data[0]).forEach(field => {
                if (isSummable(field, data[0][field])) zeroRow[field] = 0;
            });
            return zeroRow;
        });
    };

    // Fetch weekly call funnel data
    const { data: weeklyFunnel, isLoading: weeklyLoading, error: weeklyError } = useQuery(
        'weeklyFunnel',
        () => dataProvider.getList('analytics.weekly_funnel', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'week', order: 'DESC' },
            filter: {}
        })
    );

    // Fetch weekly leads data
    const { data: weeklyLeads, isLoading: leadsLoading, error: leadsError } = useQuery(
        'weeklyLeads',
        () => dataProvider.getList('analytics.weekly_leads', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'week', order: 'DESC' },
            filter: {}
        })
    );

    // Fetch campaign performance
    const { data: campaigns, isLoading: campaignsLoading, error: campaignsError } = useQuery(
        'campaigns',
        () => dataProvider.getList('analytics.campaign_performance', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'total_calls', order: 'DESC' },
            filter: {}
        })
    );

    // Fetch conversion funnel
    const { data: conversionFunnel, isLoading: funnelLoading, error: funnelError } = useQuery(
        'conversionFunnel',
        () => dataProvider.getList('analytics.conversion_funnel', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'total_leads', order: 'DESC' },
            filter: {}
        })
    );

    // Fetch daily lead metrics (last 7 days)
    const { data: dailyMetrics, isLoading: dailyLoading, error: dailyError } = useQuery(
        'dailyMetrics',
        () => dataProvider.getList('analytics.daily_lead_metrics', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'day', order: 'DESC' },
            filter: {}
        })
    );

    // Fetch qualification funnel
    const { data: qualificationFunnel, isLoading: qualFunnelLoading, error: qualFunnelError } = useQuery(
        'qualificationFunnel',
        () => dataProvider.getList('analytics.qualification_funnel', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'total_leads', order: 'DESC' },
            filter: {}
        })
    );

    // Fetch weekly qualification breakdown
    const { data: weeklyQualification, isLoading: weeklyQualLoading, error: weeklyQualError } = useQuery(
        'weeklyQualification',
        () => dataProvider.getList('analytics.weekly_lead_qualification', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'week', order: 'DESC' },
            filter: {}
        })
    );

    // Fetch disqualification reasons
    const { data: disqualReasons, isLoading: disqualLoading, error: disqualError } = useQuery(
        'disqualReasons',
        () => dataProvider.getList('analytics.disqualification_reasons', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'week', order: 'DESC' },
            filter: {}
        })
    );

    // Fetch leads with contact details
    const { data: leadsList, isLoading: leadsListLoading, error: leadsListError } = useQuery(
        'leadsList',
        () => dataProvider.getList('analytics.leads_with_calls', {
            pagination: { page: 1, perPage: 500 },
            sort: { field: 'opened_at', order: 'DESC' },
            filter: {}
        })
    );

    // Fetch qualified follow-ups
    const { data: qualifiedFollowups, isLoading: followupsLoading, error: followupsError } = useQuery(
        'qualifiedFollowups',
        () => dataProvider.getList('analytics.qualified_followups', {
            pagination: { page: 1, perPage: 500 },
            sort: { field: 'sort_order', order: 'ASC' },
            filter: {}
        })
    );

    // Pre-compute filtered leads list for pagination
    const filteredLeadsList = (leadsList?.data || [])
        .filter((item: any) => channelMatchesSource(item.source_channel, sourceFilter))
        .filter((item: any) => sourceTypeFilter === 'all' || item.source_type === sourceTypeFilter)
        .filter((item: any) => stageFilter === 'all' || (item.lead_stage || 'new') === stageFilter);

    // Dashboard summary: unique callers, qualified, won — direct from tables
    const [selYear, selMonthNum] = selectedMonth.split('-').map(Number);
    const startOfMonth = new Date(selYear, selMonthNum - 1, 1).toISOString();
    const endOfMonth = new Date(selYear, selMonthNum, 1).toISOString();

    const { data: callerStats } = useQuery(['monthlyCallers', selectedMonth], async () => {
        const { data } = await supabase
            .from('call_sessions')
            .select('from_number')
            .gte('started_at', startOfMonth)
            .lt('started_at', endOfMonth)
            .limit(5000);
        const totalCalls = data?.length || 0;
        const uniqueCallers = new Set(data?.map((r: any) => r.from_number).filter(Boolean)).size;
        return { uniqueCallers, totalCalls };
    });

    const { data: qualifiedCount } = useQuery(['monthlyQualified', selectedMonth], async () => {
        const { count } = await supabase
            .from('leads')
            .select('*', { count: 'exact', head: true })
            .not('qualified_at', 'is', null)
            .gte('qualified_at', startOfMonth)
            .lt('qualified_at', endOfMonth);
        return count || 0;
    });

    // Qualified breakdown by region and deal type
    const rpcStartDate = `${selYear}-${String(selMonthNum).padStart(2, '0')}-01`;
    const rpcEndDate = selMonthNum === 12
        ? `${selYear + 1}-01-01`
        : `${selYear}-${String(selMonthNum + 1).padStart(2, '0')}-01`;

    const { data: qualifiedBreakdown } = useQuery(['qualifiedBreakdown', selectedMonth], async () => {
        const startDate = rpcStartDate;
        const endDate = rpcEndDate;
        const { data, error } = await supabase.rpc('get_qualified_by_region', {
            p_start: startDate,
            p_end: endDate,
        });
        if (error) throw error;
        return data as { region: string; deal_type: string; qualified: number }[];
    });

    const { data: pipelineData } = useQuery(['monthlyPipeline', selectedMonth], async () => {
        const startDate = rpcStartDate;
        const endDate = rpcEndDate;
        const { data, error } = await supabase.rpc('get_monthly_pipeline', {
            p_start: startDate,
            p_end: endDate,
        });
        if (error) throw error;
        return data as { bucket: string; is_preneed: boolean; qualified: number; won: number; lost: number }[];
    });

    // Compute pipeline summary rows
    const pipelineRows = React.useMemo(() => {
        if (!pipelineData) return null;
        const atNeed = pipelineData.filter((r) => !r.is_preneed);
        const preNeed = pipelineData.filter((r) => r.is_preneed);
        const sumRows = (rows: typeof pipelineData) => ({
            qualified: rows.reduce((s, r) => s + r.qualified, 0),
            won: rows.reduce((s, r) => s + r.won, 0),
            lost: rows.reduce((s, r) => s + r.lost, 0),
        });
        const atNeedTotal = sumRows(atNeed);
        const preNeedTotal = sumRows(preNeed);
        return { atNeed, preNeed, atNeedTotal, preNeedTotal };
    }, [pipelineData]);

    // Won: contracts signed in the selected month (no attribution filter).
    // Breakdown uses funeral.region + funeral.has_preneed/has_atneed directly.
    const { data: wonData } = useQuery(['monthlyWon', selectedMonth], async () => {
        const { data } = await supabase
            .from('funerals')
            .select('region,has_preneed,has_atneed')
            .not('contracts_signed_at', 'is', null)
            .gte('contracts_signed_at', startOfMonth)
            .lt('contracts_signed_at', endOfMonth);
        return (data || []) as { region: string | null; has_preneed: boolean | null; has_atneed: boolean | null }[];
    });
    const wonStats = React.useMemo(() => {
        if (!wonData) return null;
        const regions: Record<string, { atNeed: number; preNeed: number }> = {
            quebec: { atNeed: 0, preNeed: 0 },
            ontario: { atNeed: 0, preNeed: 0 },
        };
        let atNeed = 0;
        let preNeed = 0;
        for (const row of wonData) {
            // At-need first: the fruition rule (migration 20260119120000) sets has_atneed on
            // a fulfilled preneed, and that is an at-need win. Rows with neither flag fall
            // back to at-need, so atNeed + preNeed always equals the total.
            const isPreNeed = !row.has_atneed && !!row.has_preneed;
            if (isPreNeed) preNeed += 1;
            else atNeed += 1;
            const bucket = regions[row.region ?? ''];
            if (!bucket) continue;
            if (isPreNeed) bucket.preNeed += 1;
            else bucket.atNeed += 1;
        }
        return { atNeed, preNeed, total: wonData.length, regions };
    }, [wonData]);

    const { data: agentData } = useQuery(['agentBreakdown', selectedMonth], async () => {
        const startDate = rpcStartDate;
        const endDate = rpcEndDate;
        const { data, error } = await supabase.rpc('get_agent_breakdown', {
            p_start: startDate,
            p_end: endDate,
        });
        if (error) throw error;
        return data as { agent_name: string; calls_answered: number; unique_callers: number; qualified: number; won: number; lost: number }[];
    });

    const { data: languageData } = useQuery(['languageBreakdown', selectedMonth], async () => {
        const startDate = rpcStartDate;
        const endDate = rpcEndDate;
        const { data, error } = await supabase.rpc('get_language_breakdown', {
            p_start: startDate,
            p_end: endDate,
        });
        if (error) throw error;
        return data as { language: string; calls_answered: number; unique_callers: number; qualified: number; won: number; lost: number }[];
    });

    if (weeklyLoading || leadsLoading || campaignsLoading || funnelLoading || dailyLoading || qualFunnelLoading || weeklyQualLoading || disqualLoading || leadsListLoading || followupsLoading) {
        return <Loading />;
    }

    const error = weeklyError || leadsError || campaignsError || funnelError || dailyError || qualFunnelError || weeklyQualError || disqualError || leadsListError || followupsError;
    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Title title="Leads" />
                <Alert severity="error">
                    Error loading analytics data. Please check that the analytics views are accessible.
                    <br />
                    <Typography variant="caption" component="pre" sx={{ mt: 1 }}>
                        {JSON.stringify(error, null, 2)}
                    </Typography>
                </Alert>
            </Box>
        );
    }

    const updateLeadStage = async (leadId: string, newStage: string) => {
        try {
            const updates: Record<string, any> = {
                stage: newStage,
                updated_at: new Date().toISOString(),
            };
            if (newStage === 'qualified') {
                updates.qualified_at = new Date().toISOString();
                updates.qualification_status = 'qualified';
            } else if (newStage === 'disqualified') {
                updates.disqualified_at = new Date().toISOString();
                updates.qualification_status = 'unqualified';
            }
            const { error } = await supabase
                .from('leads')
                .update(updates)
                .eq('id', leadId);
            if (error) throw error;
            notify(`Stage updated to ${newStage}`, { type: 'success' });
            await queryClient.invalidateQueries('leadsList');
        } catch (e: any) {
            notify(e.message || 'Failed to update stage', { type: 'warning' });
        }
    };


    return (
        <Box sx={{ p: 3 }}>
            <Title title="Leads" />
            
            {/* Month Picker */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <IconButton onClick={handlePrevMonth} size="small"><ChevronLeftIcon /></IconButton>
                <TextField
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => e.target.value && setSelectedMonth(e.target.value)}
                    size="small"
                    sx={{ width: 200 }}
                    inputProps={{ max: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}` }}
                />
                <IconButton onClick={handleNextMonth} size="small" disabled={isCurrentMonth}><ChevronRightIcon /></IconButton>
                {!isCurrentMonth && (
                    <Button size="small" onClick={() => setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`)}>
                        Today
                    </Button>
                )}
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateLeadOpen(true)}
                    sx={{ ml: 'auto' }}
                >
                    New Lead
                </Button>
            </Box>

            <CreateLeadDialog
                open={createLeadOpen}
                onClose={() => {
                    setCreateLeadOpen(false);
                    // Clear the deep-link param so a refresh (or the back
                    // button) doesn't reopen a dialog the agent just dismissed.
                    const url = new URL(window.location.href);
                    if (url.searchParams.has('newLead')) {
                        url.searchParams.delete('newLead');
                        window.history.replaceState({}, '', url.toString());
                    }
                }}
                onCreated={() => { void queryClient.invalidateQueries('leadsList'); }}
            />

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <PhoneIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Unique Callers</Typography>
                            </Box>
                            <Typography variant="h3">{callerStats?.uniqueCallers ?? '—'}</Typography>
                            <Typography variant="body2" color="textSecondary">
                                {selectedMonthLabel} • {callerStats?.totalCalls ?? 0} total calls
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TrendingUpIcon color="warning" sx={{ mr: 1 }} />
                                <Typography variant="h6">Qualified</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Box>
                                    <Typography variant="h3">{qualifiedCount ?? '—'}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {selectedMonthLabel}
                                    </Typography>
                                </Box>
                                {qualifiedBreakdown && qualifiedBreakdown.length > 0 && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, fontSize: '0.75rem' }}>
                                        {['quebec', 'ontario'].map((region) => {
                                            const atNeed = qualifiedBreakdown.find(r => r.region === region && r.deal_type === 'at_need')?.qualified || 0;
                                            const preNeed = qualifiedBreakdown.find(r => r.region === region && r.deal_type === 'pre_need')?.qualified || 0;
                                            const label = region === 'quebec' ? 'QC' : 'ON';
                                            return (
                                                <Box key={region} sx={{ display: 'flex', gap: 1.5 }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ minWidth: 20 }}>{label}</Typography>
                                                    <Typography variant="caption">AN {atNeed}</Typography>
                                                    <Typography variant="caption">PN {preNeed}</Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <EmojiEventsIcon color="success" sx={{ mr: 1 }} />
                                <Typography variant="h6">Won</Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
                                    {selectedMonthLabel}
                                    {wonStats ? ` • ${wonStats.total} total` : ''}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h3">{wonStats ? wonStats.atNeed : '—'}</Typography>
                                    <Typography variant="body2">At-Need</Typography>
                                    <Typography variant="caption" color="textSecondary" component="div">
                                        {wonStats
                                            ? `QC ${wonStats.regions.quebec.atNeed} · ON ${wonStats.regions.ontario.atNeed}`
                                            : '—'}
                                    </Typography>
                                </Box>
                                <Divider orientation="vertical" flexItem />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h4" color="text.secondary">
                                        {wonStats ? wonStats.preNeed : '—'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">Pre-Need</Typography>
                                    <Typography variant="caption" color="textSecondary" component="div">
                                        {wonStats
                                            ? `QC ${wonStats.regions.quebec.preNeed} · ON ${wonStats.regions.ontario.preNeed}`
                                            : '—'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Breakdown Section: Pipeline / Agent / Language */}
            {(() => {
                const clickableSx = { cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } };
                const handleBreakdownClick = (bucket: string, mode: 'qualified' | 'open', filterType: 'pipeline' | 'agent' | 'language') => {
                    setPipelineFilter({ bucket, mode, filterType });
                    setTabValue(TAB.qualifiedFollowUps);
                };
                const renderBreakdownRow = (
                    row: { label: string; qualified: number; won: number; lost?: number; extra?: React.ReactNode[] },
                    filterType: 'pipeline' | 'agent' | 'language',
                    bold?: boolean
                ) => {
                    const open = row.qualified - row.won - (row.lost || 0);
                    const cellSx = bold ? { fontWeight: 'bold', borderTop: 2, borderColor: 'divider' } : {};
                    return (
                        <TableRow key={row.label} sx={bold ? { '& td': cellSx } : undefined}>
                            <TableCell sx={cellSx}>{row.label}</TableCell>
                            {row.extra?.map((node, i) => <TableCell key={i} align="right" sx={cellSx}>{node}</TableCell>)}
                            <TableCell align="right" sx={{ ...cellSx, ...(row.qualified > 0 ? clickableSx : {}) }}
                                onClick={() => row.qualified > 0 && handleBreakdownClick(row.label, 'qualified', filterType)}>
                                {row.qualified}
                            </TableCell>
                            <TableCell align="right" sx={cellSx}>{row.won}</TableCell>
                            <TableCell align="right" sx={{ ...cellSx, ...(open > 0 ? clickableSx : {}) }}
                                onClick={() => open > 0 && handleBreakdownClick(row.label, 'open', filterType)}>
                                {open}
                            </TableCell>
                            <TableCell align="right" sx={cellSx}>
                                {row.qualified > 0 ? `${((row.won / row.qualified) * 100).toFixed(1)}%` : '—'}
                            </TableCell>
                        </TableRow>
                    );
                };
                return (
                    <Card sx={{ mb: 4 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                            onClick={() => setBreakdownExpanded(!breakdownExpanded)}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Breakdown — Pipeline / Agent / Language
                            </Typography>
                            {breakdownExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Box>
                        <Collapse in={breakdownExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={breakdownTab} onChange={(_, v) => setBreakdownTab(v)}>
                                <Tab label="Pipeline" />
                                <Tab label="By Agent" />
                                <Tab label="By Language" />
                            </Tabs>
                        </Box>

                        {/* Pipeline tab */}
                        {breakdownTab === 0 && pipelineRows && (
                            <TableContainer sx={{ p: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Timeline</TableCell>
                                            <TableCell align="right">Qualified</TableCell>
                                            <TableCell align="right">Won</TableCell>
                                            <TableCell align="right">Open</TableCell>
                                            <TableCell align="right">Conv %</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pipelineRows.atNeed.map((row) => renderBreakdownRow(
                                            { label: row.bucket, qualified: row.qualified, won: row.won, lost: row.lost }, 'pipeline'
                                        ))}
                                        {renderBreakdownRow(
                                            { label: 'At-Need Total', ...pipelineRows.atNeedTotal }, 'pipeline', true
                                        )}
                                        {pipelineRows.preNeed.map((row) => renderBreakdownRow(
                                            { label: row.bucket, qualified: row.qualified, won: row.won, lost: row.lost }, 'pipeline'
                                        ))}
                                        {renderBreakdownRow(
                                            { label: 'Pre-Need Total', ...pipelineRows.preNeedTotal }, 'pipeline', true
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Agent tab */}
                        {breakdownTab === 1 && (
                            <TableContainer sx={{ p: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Agent</TableCell>
                                            <TableCell align="right">Calls</TableCell>
                                            <TableCell align="right">Callers</TableCell>
                                            <TableCell align="right">Qualified</TableCell>
                                            <TableCell align="right">Won</TableCell>
                                            <TableCell align="right">Open</TableCell>
                                            <TableCell align="right">Conv %</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {agentData?.map((row) => renderBreakdownRow(
                                            { label: row.agent_name || 'Unknown', qualified: row.qualified, won: row.won,
                                              lost: row.lost, extra: [row.calls_answered, row.unique_callers] }, 'agent'
                                        ))}
                                        {agentData && agentData.length > 1 && (() => {
                                            const t = agentData.reduce((a, r) => ({
                                                calls_answered: a.calls_answered + r.calls_answered,
                                                unique_callers: a.unique_callers + r.unique_callers,
                                                qualified: a.qualified + r.qualified,
                                                won: a.won + r.won,
                                                lost: a.lost + r.lost,
                                            }), { calls_answered: 0, unique_callers: 0, qualified: 0, won: 0, lost: 0 });
                                            return renderBreakdownRow(
                                                { label: 'Total', qualified: t.qualified, won: t.won,
                                                  lost: t.lost, extra: [t.calls_answered, t.unique_callers] }, 'agent', true
                                            );
                                        })()}
                                    </TableBody>
                                </Table>
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    Agent assigned by first answered call per lead
                                </Typography>
                            </TableContainer>
                        )}

                        {/* Language tab */}
                        {breakdownTab === 2 && (
                            <TableContainer sx={{ p: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Language</TableCell>
                                            <TableCell align="right">Calls</TableCell>
                                            <TableCell align="right">Callers</TableCell>
                                            <TableCell align="right">Qualified</TableCell>
                                            <TableCell align="right">Won</TableCell>
                                            <TableCell align="right">Open</TableCell>
                                            <TableCell align="right">Conv %</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {languageData?.map((row) => renderBreakdownRow(
                                            { label: (row.language || 'unknown').toUpperCase(), qualified: row.qualified, won: row.won,
                                              lost: row.lost, extra: [row.calls_answered, row.unique_callers] }, 'language'
                                        ))}
                                        {languageData && languageData.length > 1 && (() => {
                                            const t = languageData.reduce((a, r) => ({
                                                calls_answered: a.calls_answered + r.calls_answered,
                                                unique_callers: a.unique_callers + r.unique_callers,
                                                qualified: a.qualified + r.qualified,
                                                won: a.won + r.won,
                                                lost: a.lost + r.lost,
                                            }), { calls_answered: 0, unique_callers: 0, qualified: 0, won: 0, lost: 0 });
                                            return renderBreakdownRow(
                                                { label: 'Total', qualified: t.qualified, won: t.won,
                                                  lost: t.lost, extra: [t.calls_answered, t.unique_callers] }, 'language', true
                                            );
                                        })()}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        </Collapse>
                    </Card>
                );
            })()}

            {/* Tabbed Content */}
            <Card>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                        <Tab label="Triage" />
                        <Tab label="Leads List" />
                        <Tab label="Qualified Follow-ups" />
                        <Tab label="Call Funnel" />
                        <Tab label="Lead Funnel" />
                        <Tab label="Lead Qualification" />
                        <Tab label="Campaign Performance" />
                        <Tab label="Conversion Rates" />
                        <Tab label="Daily Metrics" />
                    </Tabs>
                </Box>

                {/* Filters (Triage has its own) */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default', display: tabValue === TAB.triage ? 'none' : undefined }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <ToggleButtonGroup
                            value={sourceFilter}
                            exclusive
                            onChange={(_, v) => v !== null && setSourceFilter(v)}
                            size="small"
                        >
                            <ToggleButton value="all">All</ToggleButton>
                            <ToggleButton value="ad">Ad</ToggleButton>
                            <ToggleButton value="organic">Organic</ToggleButton>
                            <ToggleButton value="maps">Google Maps</ToggleButton>
                        </ToggleButtonGroup>
                        <Select
                            value={qualifiedSince}
                            onChange={(e) => { setQualifiedSince(e.target.value as any); setPipelineFilter(null); }}
                            size="small"
                            sx={{ minWidth: 140, fontSize: '0.8125rem' }}
                        >
                            <MenuItem value="month">This Month</MenuItem>
                            <MenuItem value="30">Last 30 days</MenuItem>
                            <MenuItem value="60">Last 60 days</MenuItem>
                            <MenuItem value="90">Last 90 days</MenuItem>
                            <MenuItem value="all">All Time</MenuItem>
                        </Select>
                        {tabValue === TAB.qualifiedFollowUps && (
                            <ToggleButtonGroup
                                value={typeFilter}
                                exclusive
                                onChange={(_, v) => v !== null && setTypeFilter(v)}
                                size="small"
                            >
                                <ToggleButton value="all">All</ToggleButton>
                                <ToggleButton value="at_need">At-Need</ToggleButton>
                                <ToggleButton value="pre_need">Pre-Need</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        {tabValue === TAB.qualifiedFollowUps && (
                            <ToggleButtonGroup
                                value={statusFilter}
                                exclusive
                                onChange={(_, v) => v !== null && setStatusFilter(v)}
                                size="small"
                            >
                                <ToggleButton value="all">All</ToggleButton>
                                <ToggleButton value="open">Open</ToggleButton>
                                <ToggleButton value="won">Won</ToggleButton>
                                <ToggleButton value="lost">Lost</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        {tabValue === TAB.qualifiedFollowUps && (
                            <ToggleButtonGroup
                                value={regionFilter}
                                exclusive
                                onChange={(_, v) => v !== null && setRegionFilter(v)}
                                size="small"
                            >
                                <ToggleButton value="all">All Regions</ToggleButton>
                                <ToggleButton value="quebec">QC</ToggleButton>
                                <ToggleButton value="ontario">ON</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        {tabValue === TAB.qualifiedFollowUps && (
                            <ToggleButtonGroup
                                value={languageFilter}
                                exclusive
                                onChange={(_, v) => v !== null && setLanguageFilter(v)}
                                size="small"
                            >
                                <ToggleButton value="all">All Lang</ToggleButton>
                                <ToggleButton value="en">EN</ToggleButton>
                                <ToggleButton value="fr">FR</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        {tabValue === TAB.leadsList && (
                            <ToggleButtonGroup
                                value={sourceTypeFilter}
                                exclusive
                                onChange={(_, v) => { if (v !== null) { setSourceTypeFilter(v); setLeadsPage(0); } }}
                                size="small"
                            >
                                <ToggleButton value="all">All</ToggleButton>
                                <ToggleButton value="call">Calls</ToggleButton>
                                <ToggleButton value="form">Forms</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        {tabValue === TAB.leadsList && (
                            <ToggleButtonGroup
                                value={stageFilter}
                                exclusive
                                onChange={(_, v) => { if (v !== null) { setStageFilter(v); setLeadsPage(0); } }}
                                size="small"
                            >
                                <ToggleButton value="all">All</ToggleButton>
                                <ToggleButton value="new">No Action</ToggleButton>
                                <ToggleButton value="spoken">Spoken</ToggleButton>
                                <ToggleButton value="qualified">Qualified</ToggleButton>
                                <ToggleButton value="disqualified">Disqualified</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                        {pipelineFilter && (
                            <Chip
                                label={`${pipelineFilter.bucket} — ${pipelineFilter.mode === 'qualified' ? 'All Qualified' : 'Open'}`}
                                color="primary"
                                size="small"
                                onDelete={() => setPipelineFilter(null)}
                            />
                        )}
                        {(pipelineFilter || sourceFilter !== 'all' || qualifiedSince !== 'all' || typeFilter !== 'all' || statusFilter !== 'all' || stageFilter !== 'all' || sourceTypeFilter !== 'all' || regionFilter !== 'all' || languageFilter !== 'all') && (
                            <Button
                                size="small"
                                startIcon={<ClearIcon />}
                                onClick={() => {
                                    setPipelineFilter(null);
                                    setSourceFilter('all');
                                    setQualifiedSince('all');
                                    setTypeFilter('all');
                                    setStatusFilter('all');
                                    setStageFilter('all');
                                    setSourceTypeFilter('all');
                                    setRegionFilter('all');
                                    setLanguageFilter('all');
                                }}
                            >
                                Clear
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Tab 1: Qualified Follow-ups */}
                <TabPanel value={tabValue} index={TAB.qualifiedFollowUps}>
                    {(() => {
                        let filtered = qualifiedFollowups?.data || [];
                        // Source filter
                        if (sourceFilter !== 'all') {
                            filtered = filtered.filter((item: any) => channelMatchesSource(item.source_channel, sourceFilter));
                        }
                        // Qualified since filter
                        if (pipelineFilter) {
                            // Breakdown click forces current month
                            filtered = filtered.filter((item: any) => {
                                if (!item.qualified_at) return false;
                                return item.qualified_at >= startOfMonth && item.qualified_at < endOfMonth;
                            });
                        } else if (qualifiedSince !== 'all') {
                            const sinceDate = qualifiedSince === 'month'
                                ? startOfMonth
                                : new Date(Date.now() - parseInt(qualifiedSince) * 86400000).toISOString();
                            filtered = filtered.filter((item: any) =>
                                item.qualified_at && item.qualified_at >= sinceDate
                            );
                        }
                        // Type filter
                        if (typeFilter !== 'all') {
                            filtered = filtered.filter((item: any) => item.deal_type === typeFilter);
                        }
                        // Status filter
                        if (statusFilter === 'open') {
                            filtered = filtered.filter((item: any) => !item.contracts_signed_at && item.lead_stage !== 'closed_lost');
                        } else if (statusFilter === 'won') {
                            filtered = filtered.filter((item: any) => !!item.contracts_signed_at);
                        } else if (statusFilter === 'lost') {
                            filtered = filtered.filter((item: any) => item.lead_stage === 'closed_lost');
                        }
                        // Region filter
                        if (regionFilter !== 'all') {
                            filtered = filtered.filter((item: any) => item.region === regionFilter);
                        }
                        // Language filter
                        if (languageFilter !== 'all') {
                            filtered = filtered.filter((item: any) => item.language === languageFilter);
                        }
                        // Breakdown click-through filters
                        if (pipelineFilter) {
                            const { bucket, mode, filterType } = pipelineFilter;
                            if (filterType === 'agent') {
                                const isTotal = bucket === 'Total';
                                if (!isTotal) {
                                    filtered = filtered.filter((item: any) =>
                                        (item.first_call_agent || 'Unknown') === bucket
                                    );
                                }
                            } else if (filterType === 'language') {
                                const isTotal = bucket === 'Total';
                                if (!isTotal) {
                                    filtered = filtered.filter((item: any) =>
                                        (item.language || 'unknown').toUpperCase() === bucket
                                    );
                                }
                            } else {
                                // Pipeline: filter by timeline bucket
                                const isTotal = bucket === 'At-Need Total' || bucket === 'Pre-Need Total';
                                filtered = filtered.filter((item: any) => {
                                    const isPreneed = item.deal_type === 'pre_need';
                                    const leadBucket = isPreneed ? 'Pre-Need' : (item.timeline || '?');
                                    if (isTotal) {
                                        const wantPreneed = bucket === 'Pre-Need Total';
                                        if (wantPreneed !== isPreneed) return false;
                                    } else {
                                        if (leadBucket !== bucket) return false;
                                    }
                                    return true;
                                });
                            }
                            // Apply open/qualified mode from breakdown click
                            if (mode === 'open') {
                                filtered = filtered.filter((item: any) => !item.contracts_signed_at && item.lead_stage !== 'closed_lost');
                            }
                        }
                        return (<>
                    <Typography variant="h6" gutterBottom>
                        Qualified Leads ({filtered.length})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Timeline</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Market</TableCell>
                                    <TableCell>Qualified</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Last Call</TableCell>
                                    <TableCell>Days Since</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.slice(qualifiedPage * rowsPerPage, qualifiedPage * rowsPerPage + rowsPerPage).map((lead: any, idx: number) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            <span
                                                onClick={() => { setDrawerLeadId(lead.lead_id); setDrawerOpen(true); }}
                                                style={{ color: '#1976d2', fontWeight: 500, cursor: 'pointer' }}
                                            >
                                                {lead.contact_name || 'Unknown'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center">
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {lead.contact_phone || 'N/A'}
                                                </Typography>
                                                {lead.contact_phone && <AircallButton phoneNumber={lead.contact_phone} />}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {lead.deal_type ? (
                                                <Chip
                                                    label={lead.deal_type === 'at_need' ? 'At-Need' : 'Pre-Need'}
                                                    size="small"
                                                    color={lead.deal_type === 'at_need' ? 'error' : 'success'}
                                                />
                                            ) : (
                                                <Chip label="—" size="small" variant="outlined" disabled />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {lead.timeline ? (
                                                <Chip
                                                    label={lead.timeline}
                                                    size="small"
                                                    color={
                                                        lead.timeline === 'Passed' ? 'error'
                                                        : lead.timeline.startsWith('Imminent') ? 'warning'
                                                        : lead.timeline.startsWith('Near') ? 'info'
                                                        : 'default'
                                                    }
                                                />
                                            ) : (
                                                <Chip label="—" size="small" variant="outlined" disabled />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={channelLabel(lead.source_channel || 'unknown')}
                                                size="small"
                                                color={lead.source_channel === 'ad' ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const regionLabel = lead.region === 'quebec' ? 'QC' : lead.region === 'ontario' ? 'ON' : null;
                                                const langLabel = lead.language ? lead.language.toUpperCase() : null;
                                                if (!regionLabel && !langLabel) {
                                                    return <Chip label="—" size="small" variant="outlined" disabled />;
                                                }
                                                const label = [regionLabel, langLabel].filter(Boolean).join(' ');
                                                return (
                                                    <Chip
                                                        label={label}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: lead.language === 'fr' ? '#0055A4' : '#C8102E',
                                                            color: 'white',
                                                            fontWeight: 'bold'
                                                        }}
                                                    />
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {lead.qualified_at ? new Date(lead.qualified_at).toLocaleDateString() : 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {lead.lead_stage === 'closed_lost' ? (
                                                <Chip
                                                    label="Closed Lost"
                                                    size="small"
                                                    color="error"
                                                />
                                            ) : lead.quote_id ? (
                                                lead.contracts_signed_at ? (
                                                    <Chip
                                                        label="Closed Won"
                                                        size="small"
                                                        color="success"
                                                        clickable
                                                        onClick={() => window.open(`/funerals/${lead.funeral_id}`, '_blank')}
                                                    />
                                                ) : lead.funeral_id ? (
                                                    <Chip
                                                        label="Form Filled"
                                                        size="small"
                                                        color="warning"
                                                        clickable
                                                        onClick={() => window.open(`/funerals/${lead.funeral_id}`, '_blank')}
                                                    />
                                                ) : (
                                                    <Chip
                                                        label="Quoted"
                                                        size="small"
                                                        color="info"
                                                        clickable
                                                        onClick={() => window.open(`/quotes/${lead.quote_id}/show`, '_blank')}
                                                    />
                                                )
                                            ) : (
                                                <Chip label="—" size="small" variant="outlined" disabled />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                {lead.last_call_at ? new Date(lead.last_call_at).toLocaleString() : 'Never'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {lead.days_since_last_call !== null && lead.days_since_last_call !== undefined ? (
                                                <Chip
                                                    label={`${Math.floor(lead.days_since_last_call)}d`}
                                                    size="small"
                                                    color={lead.days_since_last_call > 7 ? 'error' : lead.days_since_last_call > 3 ? 'warning' : 'default'}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="textSecondary">—</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {lead.quote_id && !lead.contracts_signed_at && lead.lead_stage !== 'closed_lost' && (
                                                <Chip
                                                    label="Close Lost"
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    clickable
                                                    onClick={() => setCloseLostLead(lead)}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={filtered.length}
                            page={qualifiedPage}
                            onPageChange={(_, p) => setQualifiedPage(p)}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[rowsPerPage]}
                        />
                    </TableContainer>
                        </>);
                    })()}
                </TabPanel>

                {/* Tab 2: Call Funnel */}
                <TabPanel value={tabValue} index={TAB.callFunnel}>
                    <Typography variant="h6" gutterBottom>
                        Weekly Call Funnel (Last 12 Weeks)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Week</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell align="right">Total Calls</TableCell>
                                    <TableCell align="right">Answered</TableCell>
                                    <TableCell align="right">Spoken (≥30s)</TableCell>
                                    <TableCell align="right">Unique Callers</TableCell>
                                    <TableCell align="right">Answer Rate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filterBySource(weeklyFunnel?.data)?.slice(0, 12).map((row: any, idx: number) => {
                                    const answerRate = row.total_calls > 0 
                                        ? ((row.answered_calls / row.total_calls) * 100).toFixed(1)
                                        : '0';
                                    return (
                                        <TableRow key={idx}>
                                            <TableCell>{new Date(row.week).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={channelLabel(row.source_channel || 'unknown')} 
                                                    size="small"
                                                    color={row.source_channel === 'ad' ? 'primary' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">{row.total_calls}</TableCell>
                                            <TableCell align="right">{row.answered_calls}</TableCell>
                                            <TableCell align="right">{row.spoken_calls}</TableCell>
                                            <TableCell align="right">{row.unique_callers}</TableCell>
                                            <TableCell align="right">{answerRate}%</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Tab 3: Lead Funnel */}
                <TabPanel value={tabValue} index={TAB.leadFunnel}>
                    <Typography variant="h6" gutterBottom>
                        Weekly Lead Funnel (Last 12 Weeks)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Week</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell align="right">Opened</TableCell>
                                    <TableCell align="right">Spoken</TableCell>
                                    <TableCell align="right">Qualified</TableCell>
                                    <TableCell align="right">Won</TableCell>
                                    <TableCell align="right">Lost</TableCell>
                                    <TableCell align="right">Conversion</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filterBySource(weeklyLeads?.data)?.slice(0, 12).map((row: any, idx: number) => {
                                    const conversionRate = row.leads_opened > 0
                                        ? ((row.leads_won / row.leads_opened) * 100).toFixed(1)
                                        : '0';
                                    return (
                                        <TableRow key={idx}>
                                            <TableCell>{new Date(row.week).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={channelLabel(row.source_channel || 'unknown')} 
                                                    size="small"
                                                    color={row.source_channel === 'ad' ? 'primary' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">{row.leads_opened}</TableCell>
                                            <TableCell align="right">{row.leads_spoken}</TableCell>
                                            <TableCell align="right">{row.leads_qualified}</TableCell>
                                            <TableCell align="right">{row.leads_won}</TableCell>
                                            <TableCell align="right">{row.leads_lost}</TableCell>
                                            <TableCell align="right">{conversionRate}%</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Tab 0: Leads List */}
                <TabPanel value={tabValue} index={TAB.leadsList}>
                    <Typography variant="h6" gutterBottom>
                        All Leads (Most Recent 500)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Opened</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Language</TableCell>
                                    <TableCell align="right">Calls</TableCell>
                                    <TableCell>Stage</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredLeadsList.slice(leadsPage * rowsPerPage, leadsPage * rowsPerPage + rowsPerPage).map((row: any, idx: number) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            {row.opened_at 
                                                ? new Date(row.opened_at).toLocaleString()
                                                : 'N/A'
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <span
                                                onClick={() => { setDrawerLeadId(row.lead_id); setDrawerOpen(true); }}
                                                style={{ color: '#1976d2', fontWeight: 500, cursor: 'pointer' }}
                                            >
                                                {row.contact_name || 'Unknown'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center">
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {row.contact_phone || 'N/A'}
                                                </Typography>
                                                {row.contact_phone && <AircallButton phoneNumber={row.contact_phone} />}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Chip
                                                    label={channelLabel(row.source_channel || 'unknown')}
                                                    size="small"
                                                    color={row.source_channel === 'ad' ? 'primary' : 'default'}
                                                />
                                                {row.source_type === 'form' && (
                                                    <DescriptionIcon sx={{ fontSize: 16, color: row.intent === 'at-need' ? 'success.main' : row.intent === 'pre-need' ? 'warning.main' : 'text.secondary' }} titleAccess={`Form submission${row.intent ? ` (${row.intent})` : ''}`} />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            {row.language ? (
                                                <Chip
                                                    label={row.language.toUpperCase()} 
                                                    size="small"
                                                    sx={{
                                                        bgcolor: row.language === 'fr' ? '#0055A4' : '#C8102E',
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                            ) : (
                                                <Chip 
                                                    label="—" 
                                                    size="small"
                                                    variant="outlined"
                                                    disabled
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {row.total_calls || 0}
                                                {row.spoken_calls > 0 && ` (${row.spoken_calls} spoken)`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Select
                                                value={row.lead_stage || 'new'}
                                                size="small"
                                                onChange={(e) => updateLeadStage(row.lead_id, e.target.value)}
                                                sx={{
                                                    minWidth: 130,
                                                    fontSize: '0.8125rem',
                                                    '& .MuiSelect-select': { py: 0.5 },
                                                    bgcolor: row.lead_stage === 'qualified' ? 'success.light' :
                                                        row.lead_stage === 'disqualified' ? 'error.light' :
                                                        row.lead_stage === 'spoken' ? 'info.light' :
                                                        'transparent',
                                                    color: row.lead_stage === 'qualified' ? 'success.contrastText' :
                                                        row.lead_stage === 'disqualified' ? 'error.contrastText' :
                                                        row.lead_stage === 'spoken' ? 'info.contrastText' :
                                                        'text.primary',
                                                }}
                                            >
                                                <MenuItem value="new">New</MenuItem>
                                                <MenuItem value="callback_needed">Callback Needed</MenuItem>
                                                <MenuItem value="spoken">Spoken</MenuItem>
                                                <MenuItem value="qualified">Qualified</MenuItem>
                                                <MenuItem value="disqualified">Disqualified</MenuItem>
                                                <MenuItem value="closed_won">Won</MenuItem>
                                                <MenuItem value="closed_lost">Lost</MenuItem>
                                                <MenuItem value="spam">Spam</MenuItem>
                                            </Select>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={filteredLeadsList.length}
                            page={leadsPage}
                            onPageChange={(_, p) => setLeadsPage(p)}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[rowsPerPage]}
                        />
                    </TableContainer>
                </TabPanel>

                {/* Tab 4: Lead Qualification */}
                <TabPanel value={tabValue} index={TAB.leadQualification}>
                    <Typography variant="h6" gutterBottom>
                        Lead Qualification Funnel
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Source</TableCell>
                                    <TableCell align="right">Total Leads</TableCell>
                                    <TableCell align="right">Pending</TableCell>
                                    <TableCell align="right">Qualified</TableCell>
                                    <TableCell align="right">Unqualified</TableCell>
                                    <TableCell align="right">Won</TableCell>
                                    <TableCell align="right">Qualification Rate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filterBySource(qualificationFunnel?.data)?.map((row: any, idx: number) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            <Chip 
                                                label={channelLabel(row.source_channel || 'unknown')} 
                                                size="small"
                                                color={row.source_channel === 'ad' ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">{row.total_leads}</TableCell>
                                        <TableCell align="right">
                                            <Chip label={row.pending_count} size="small" color="default" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip label={row.qualified_count} size="small" color="success" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip label={row.unqualified_count} size="small" color="error" />
                                        </TableCell>
                                        <TableCell align="right">{row.won_count}</TableCell>
                                        <TableCell align="right">{row.qualified_rate}%</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                        Weekly Qualification Breakdown (Last 8 Weeks)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Week</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Lead Count</TableCell>
                                    <TableCell align="right">Unique Contacts</TableCell>
                                    <TableCell align="right">Avg Hours to Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filterBySource(weeklyQualification?.data)?.slice(0, 24).map((row: any, idx: number) => {
                                    const avgHours = row.qualification_status === 'qualified'
                                        ? row.avg_hours_to_qualify
                                        : row.avg_hours_to_disqualify;
                                    return (
                                        <TableRow key={idx}>
                                            <TableCell>{new Date(row.week).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={channelLabel(row.source_channel || 'unknown')} 
                                                    size="small"
                                                    color={row.source_channel === 'ad' ? 'primary' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={row.qualification_status} 
                                                    size="small"
                                                    color={
                                                        row.qualification_status === 'qualified' ? 'success' :
                                                        row.qualification_status === 'unqualified' ? 'error' :
                                                        'default'
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell align="right">{row.lead_count}</TableCell>
                                            <TableCell align="right">{row.unique_contacts}</TableCell>
                                            <TableCell align="right">
                                                {avgHours ? `${avgHours.toFixed(1)}h` : 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                        Disqualification Reasons (Last 8 Weeks)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Week</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Reason</TableCell>
                                    <TableCell align="right">Count</TableCell>
                                    <TableCell align="right">Unique Contacts</TableCell>
                                    <TableCell align="right">Avg Hours to Disqualify</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filterBySource(disqualReasons?.data)?.slice(0, 20).map((row: any, idx: number) => (
                                    <TableRow key={idx}>
                                        <TableCell>{new Date(row.week).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={channelLabel(row.source_channel || 'unknown')} 
                                                size="small"
                                                color={row.source_channel === 'ad' ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={row.disqualified_reason} 
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">{row.disqualified_count}</TableCell>
                                        <TableCell align="right">{row.unique_contacts}</TableCell>
                                        <TableCell align="right">
                                            {row.avg_hours_to_disqualify 
                                                ? `${row.avg_hours_to_disqualify.toFixed(1)}h` 
                                                : 'N/A'
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Tab 5: Campaign Performance */}
                <TabPanel value={tabValue} index={TAB.campaignPerformance}>
                    <Typography variant="h6" gutterBottom>
                        Campaign Performance
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Campaign</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell align="right">Total Calls</TableCell>
                                    <TableCell align="right">Answered</TableCell>
                                    <TableCell align="right">Spoken</TableCell>
                                    <TableCell align="right">Unique GCLIDs</TableCell>
                                    <TableCell align="right">Avg Duration</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filterBySource(campaigns?.data)?.map((row: any, idx: number) => (
                                    <TableRow key={idx}>
                                        <TableCell>{row.utm_campaign || 'N/A'}</TableCell>
                                        <TableCell>{row.utm_source || 'N/A'}</TableCell>
                                        <TableCell align="right">{row.total_calls}</TableCell>
                                        <TableCell align="right">{row.answered_calls}</TableCell>
                                        <TableCell align="right">{row.spoken_calls}</TableCell>
                                        <TableCell align="right">{row.unique_gclids}</TableCell>
                                        <TableCell align="right">
                                            {row.avg_duration_seconds 
                                                ? `${Math.round(row.avg_duration_seconds)}s`
                                                : 'N/A'
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Tab 6: Conversion Funnel */}
                <TabPanel value={tabValue} index={TAB.conversionRates}>
                    <Typography variant="h6" gutterBottom>
                        Conversion Funnel (Last 90 Days)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Campaign</TableCell>
                                    <TableCell align="right">Total Leads</TableCell>
                                    <TableCell align="right">Spoken</TableCell>
                                    <TableCell align="right">Qualified</TableCell>
                                    <TableCell align="right">Won</TableCell>
                                    <TableCell align="right">Spoken Rate</TableCell>
                                    <TableCell align="right">Qualified Rate</TableCell>
                                    <TableCell align="right">Close Rate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filterBySource(conversionFunnel?.data)?.map((row: any, idx: number) => (
                                    <TableRow key={idx}>
                                        <TableCell>
                                            {row.utm_campaign || 'All Campaigns'}
                                            <br />
                                            <Chip 
                                                label={channelLabel(row.source_channel || 'unknown')} 
                                                size="small"
                                                color={row.source_channel === 'ad' ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">{row.total_leads}</TableCell>
                                        <TableCell align="right">{row.spoken_leads}</TableCell>
                                        <TableCell align="right">{row.qualified_leads}</TableCell>
                                        <TableCell align="right">{row.won_leads}</TableCell>
                                        <TableCell align="right">{row.spoken_rate_pct}%</TableCell>
                                        <TableCell align="right">{row.qualified_rate_pct}%</TableCell>
                                        <TableCell align="right">{row.close_rate_pct}%</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Tab 8: Triage */}
                <TabPanel value={tabValue} index={TAB.triage}>
                    <LeadsTriage />
                </TabPanel>

                {/* Tab 7: Daily Metrics */}
                <TabPanel value={tabValue} index={TAB.dailyMetrics}>
                    <Typography variant="h6" gutterBottom>
                        Daily Lead Metrics (Last 7 Days)
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell align="right">New Leads</TableCell>
                                    <TableCell align="right">Callback Needed</TableCell>
                                    <TableCell align="right">Spoken</TableCell>
                                    <TableCell align="right">Qualified</TableCell>
                                    <TableCell align="right">Avg Response (min)</TableCell>
                                    <TableCell align="right">Median Response (min)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filterBySource(dailyMetrics?.data)?.slice(0, 14).map((row: any, idx: number) => (
                                    <TableRow key={idx}>
                                        <TableCell>{new Date(row.day).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={channelLabel(row.source_channel || 'unknown')} 
                                                size="small"
                                                color={row.source_channel === 'ad' ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">{row.new_leads}</TableCell>
                                        <TableCell align="right">{row.callback_needed}</TableCell>
                                        <TableCell align="right">{row.spoken}</TableCell>
                                        <TableCell align="right">{row.qualified}</TableCell>
                                        <TableCell align="right">
                                            {row.avg_minutes_to_spoken 
                                                ? Math.round(row.avg_minutes_to_spoken)
                                                : 'N/A'
                                            }
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.median_minutes_to_spoken 
                                                ? Math.round(row.median_minutes_to_spoken)
                                                : 'N/A'
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

            </Card>

            {/* Close Lost Reason Modal */}
            <Dialog
                open={!!closeLostLead}
                onClose={() => {
                    setCloseLostLead(null);
                    setCloseLostReason('');
                    setCloseLostNotes('');
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Close Lost — {closeLostLead?.contact_name || ''}
                </DialogTitle>
                <DialogContent>
                    <FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
                        <FormLabel component="legend">Reason *</FormLabel>
                        <RadioGroup
                            value={closeLostReason}
                            onChange={(e) => setCloseLostReason(e.target.value)}
                        >
                            <FormControlLabel value="price" control={<Radio />} label="Found lower price" />
                            <FormControlLabel value="competitor" control={<Radio />} label="Went with competitor" />
                            <FormControlLabel value="traditional" control={<Radio />} label="Traditional Funeral Home" />
                            <FormControlLabel value="no_reply" control={<Radio />} label="No Reply" />
                            <FormControlLabel value="out_of_area" control={<Radio />} label="Out of Service Area" />
                            <FormControlLabel value="other" control={<Radio />} label="Other" />
                        </RadioGroup>
                    </FormControl>
                    <TextField
                        label={closeLostReason === 'other' ? 'Notes *' : 'Notes'}
                        multiline
                        minRows={2}
                        fullWidth
                        value={closeLostNotes}
                        onChange={(e) => setCloseLostNotes(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                    {closeLostLead?.funeral_id && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            This lead has a funeral. Confirming will also archive the funeral and hide it from the funerals list.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setCloseLostLead(null);
                            setCloseLostReason('');
                            setCloseLostNotes('');
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={!closeLostReason || (closeLostReason === 'other' && !closeLostNotes.trim())}
                        onClick={async () => {
                            try {
                                const { error } = await supabase
                                    .from('leads')
                                    .update({
                                        stage: 'closed_lost',
                                        closed_lost_reason: closeLostReason,
                                        closed_lost_notes: closeLostNotes.trim() || null,
                                        updated_at: new Date().toISOString(),
                                    })
                                    .eq('id', closeLostLead.lead_id);
                                if (error) throw error;
                                if (closeLostLead.funeral_id) {
                                    const { error: funeralError } = await supabase
                                        .from('funerals')
                                        .update({ archived_at: new Date().toISOString() })
                                        .eq('id', closeLostLead.funeral_id);
                                    if (funeralError) throw funeralError;
                                }
                                notify(
                                    closeLostLead.funeral_id
                                        ? 'Lead closed as lost and funeral archived'
                                        : 'Lead closed as lost',
                                    { type: 'success' }
                                );
                                await queryClient.invalidateQueries('qualifiedFollowups');
                                setCloseLostLead(null);
                                setCloseLostReason('');
                                setCloseLostNotes('');
                            } catch (e: any) {
                                notify(e.message || 'Failed to close lead', { type: 'warning' });
                            }
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <LeadDrawer
                leadId={drawerLeadId}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
            />
        </Box>
    );
};

